import { useClientFetch } from "../hooks/useClientFetch.js";
import useReloadIfSearchStoreMissing, { isSearchStoreInitial } from "../hooks/useReloadIfSearchStoreMissing.js";
import { useSearchAPIParams } from "../hooks/useSearchAPIParams.js";
import React, { Profiler, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSearch } from "../store/search/selectors.js";
import { actions as LoaderActions } from "../store/loadingScreen/actions.js";
import { actions as SearchResultsBoundsActions } from "../store/searchResultsBounds/actions.js";
import { actions as SearchCriteriaActions } from "../store/searchCriteria/actions.js";
import { actions as brandedFaresActions } from "../store/brandedFareOffers/actions.js";
import { fetchAndFilterByBrandedFares } from "../branded-fares/utils/fetchAndFilterBrandedFares.js";
import { Route, useLocation, useParams } from "react-router-dom";
import useRouteName from "../hooks/useRouteName.js";
import StaticContext from "../app/StaticContext/StaticContext.js";
import useClientMetrics from "../hooks/useClientMetrics.js";
import { PageHeader } from "./Search.js";
import { AppShellDesktop } from "../components/elements/AppShell/index.js";
import Container from "../components/elements/Container/index.js";
import { Box, HiddenVisually, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useSearchbox from "../hooks/useSearchbox.js";
import useLocaleContext from "../hooks/useLocaleContext.js";
import { SearchboxController, SearchBoxHorizontalDefault } from "@bookingcom/flights-searchbox";
import useRemoveExternalParamsFromUrl from "../hooks/useRemoveExternalParamsFromUrl.js";
import { trackWithDefaultStage } from "@flights/et-universal";
import useFareCalendarEligible from "../hooks/useFareCalendarEligible.js";
import { getSearchCriteria } from "../store/getSearchCriteria.js";
import { getSearchFetchStatus } from "../store/searchResultsBounds/inboundSelectors.js";
import SearchLoadingScreen from "../components/elements/LoadingScreen/components/SearchLoadingScreen.js";
import SearchResultsInboundDesktop from "../components/elements/SearchResults/SearchResults.inbound.desktop.js";
import { FLIGHT_DETAILS_INBOUND_ROUTE_PATH } from "@flights/constants";
import { HomeDesktop, FlightDetailsDesktop, SearchOutboundDesktop } from "../app/routes.js";
import useMeasureSearchToFlightDetails from "../hooks/useMeasureSearchToFlightDetails.js";
import { getMarketingTrackingVariable, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import useTrackPageLoad from "../hooks/useTrackPageLoad.js";
import { getVisible } from "../store/loadingScreen/selectors.js";
import { useSpringDeal } from "../hooks/useSpringDeal.js";
import { useSummerDeal } from "../hooks/useSummerDeal.js";
import useGlobalContext from "../hooks/useGlobalContext.js";
import useProfiler from "../hooks/useProfiler.js";
import { WishlistWidgetLoader } from "../components/remote/index.js";
const SearchInboundDesktop = ({ getInitialState }) => {
    const search = useSelector(getSearch);
    const fetchStatus = useSelector(getSearchFetchStatus);
    const loadingScreenVisible = useSelector(getVisible);
    const dispatch = useDispatch();
    const location = useLocation();
    const routeName = useRouteName();
    const staticContext = useContext(StaticContext);
    const i18n = useI18n();
    const [lastTrackedPageLoad, setLastTrackedPageLoad] = useState("");
    const { trackRenderTime } = useClientMetrics();
    const trackSRContextEventV2 = useTrackPageLoad("search_results_inbound");
    const trackSRSkeletonContextEventV2 = useTrackPageLoad("search_results_skeleton");
    const trackSRModalContextEventV2 = useTrackPageLoad("search_results_modal");
    const { onFetchedResults, onSelectFlight } = useMeasureSearchToFlightDetails();
    const { isSpringDealExp } = useSpringDeal();
    const { isSummerDealExp } = useSummerDeal();
    const { currencyCode, features } = useGlobalContext();
    const { isLoggedInAuthLow } = useProfiler();
    const isFareCalendarEligible = useFareCalendarEligible();
    const queryParams = new URLSearchParams(location.search);
    if (getInitialState && staticContext.set) {
        staticContext.set({
            getInitialState: getInitialState(queryParams, { selectedBound: "inbound" })
        });
    }
    useRemoveExternalParamsFromUrl();
    const params = useParams();
    const searchAPIParams = useSearchAPIParams({ inboundFor: params.outboundToken });
    const url = `/api/flights/?${searchAPIParams}`;
    const isMetaOrigin = getMarketingTrackingVariable("ext-origin");
    const outboundBrandedFareIdx = queryParams.get("outboundBrandedFareIdx") ?? undefined;
    const outboundBrandedFareName = queryParams.get("outboundBrandedFareName") ?? undefined;
    const outboundBrandedFareTravellerPrice = queryParams.get("outboundBrandedFareTravellerPrice") ?? undefined;
    useClientFetch(url, {
        delayFetchBy: !!isMetaOrigin && routeName.includes("searchresults") ? 500 : 0,
        condition: () => {
            if (isSearchStoreInitial(search)) {
                return false;
            }
            return true;
        },
        fetch: () => {
            dispatch(SearchResultsBoundsActions.fetchInbound());
        },
        success: (data) => {
            dispatch(SearchCriteriaActions.setSearchCriteria(getSearchCriteria(queryParams, search)));
            const shouldFilterByFare = outboundBrandedFareIdx !== undefined;
            if (!shouldFilterByFare) {
                dispatch(SearchResultsBoundsActions.fetchSuccessInbound({
                    ...data,
                    flightOffers: data.flightOffers.map((f) => ({ ...f, segments: [f.segments[1]] }))
                }));
                dispatch(LoaderActions.hide());
                onFetchedResults();
                return;
            }
            fetchAndFilterByBrandedFares(data.flightOffers, {
                selectedFareName: outboundBrandedFareName,
                selectedFareTravellerPrice: outboundBrandedFareTravellerPrice
            })
                .then(({ filteredOffers: offersWithHiddenFlags, brandedFaresMap }) => {
                brandedFaresMap.forEach(({ brandedFares }, offerToken) => {
                    dispatch(brandedFaresActions.fetchSuccess(brandedFares, offerToken));
                });
                offersWithHiddenFlags.forEach((offer) => {
                    if (offer.brandedFareOffers && !brandedFaresMap.has(offer.token)) {
                        dispatch(brandedFaresActions.fetchSuccess(offer.brandedFareOffers, offer.token));
                    }
                });
                dispatch(SearchResultsBoundsActions.fetchSuccessInbound({
                    ...data,
                    flightOffers: offersWithHiddenFlags.map((f) => ({ ...f, segments: [f.segments[1]] }))
                }));
                dispatch(LoaderActions.hide());
                onFetchedResults();
            })
                .catch(() => {
                dispatch(SearchResultsBoundsActions.fetchSuccessInbound({
                    ...data,
                    flightOffers: data.flightOffers.map((f) => ({ ...f, segments: [f.segments[1]] }))
                }));
                dispatch(LoaderActions.hide());
                onFetchedResults();
            });
        },
        error: (data) => {
            dispatch(LoaderActions.hide());
            dispatch(SearchResultsBoundsActions.fetchErrorInbound(data));
        },
        forceRefetch: location?.state?.forceRefetch
    });
    useEffect(() => {
        if (["initial", "loading"].includes(fetchStatus)) {
            if (loadingScreenVisible && lastTrackedPageLoad !== "search_results_modal") {
                trackSRModalContextEventV2();
                setLastTrackedPageLoad("search_results_modal");
            }
            else if (lastTrackedPageLoad !== "search_results_skeleton") {
                trackSRSkeletonContextEventV2();
                setLastTrackedPageLoad("search_results_skeleton");
            }
        }
        else if (lastTrackedPageLoad !== "search_results_inbound") {
            trackSRContextEventV2();
            setLastTrackedPageLoad("search_results_inbound");
        }
    }, [fetchStatus]);
    const searchBoxProps = useSearchbox();
    const { isRTL } = useLocaleContext();
    const searchBoxControllerProps = { ...searchBoxProps, i18n, defaultSegmentLength: search.searchSegments.length };
    const [preloadDetails, toggle] = useState(false);
    if (!preloadDetails && fetchStatus === "success") {
        toggle(true);
        SearchOutboundDesktop.preload();
        FlightDetailsDesktop.preload();
        HomeDesktop.preload();
    }
    if (useReloadIfSearchStoreMissing(search)) {
        return null;
    }
    return (React.createElement(Profiler, { id: "screen", onRender: trackRenderTime },
        React.createElement(PageHeader, { segments: search.searchSegments }),
        React.createElement(AppShellDesktop, null,
            React.createElement(Stack, { grow: true, gap: 6 },
                React.createElement(Box, { padding: 0, backgroundColor: "neutral_alt", mixin: { paddingBlockStart: 6, paddingBlockEnd: 6 } },
                    React.createElement(Container, null,
                        React.createElement(HiddenVisually, null,
                            React.createElement(Text, { tagName: "h1" }, i18n.trans(t("a11y_flights_sr_search_hidden_heading")))),
                        React.createElement(Stack, { gap: 2 },
                            React.createElement(SearchboxController, { ...searchBoxControllerProps, isRTL: isRTL, isResponsiveFixes: true, enabledCountriesAndAnywhere: true, isSpringDealExp: isSpringDealExp, isSummerDealExp: isSummerDealExp, isSearchAnyCityEnabled: true, fareCalendarConfig: {
                                    isFareCalendarDesktopEnabled: isFareCalendarEligible &&
                                        (!!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1) ||
                                            !!trackWithDefaultStage("flights_web_cat_fare_calendar_data_collection", 1)),
                                    isFareCalendarDisplayEnabled: isFareCalendarEligible && !!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1),
                                    currencyCode
                                }, isCalendarReflowFixEnabled: isFeatureRunningClientSide("FLIGHTS_WEB_CAT_CALENDAR_FIX"), showCabinClassLabel: !!trackWithDefaultStage("flights_cat_cabin_class_label", 1) }, () => React.createElement(SearchBoxHorizontalDefault, null))))),
                React.createElement(Container, null,
                    React.createElement(SearchResultsInboundDesktop, { outboundBrandedFareName: outboundBrandedFareName, onSelectFlight: onSelectFlight })),
                React.createElement(Route, { key: FLIGHT_DETAILS_INBOUND_ROUTE_PATH, path: FLIGHT_DETAILS_INBOUND_ROUTE_PATH, exact: true },
                    React.createElement(FlightDetailsDesktop, null)),
                routeName.includes("searchresults") && React.createElement(SearchLoadingScreen, null),
                features["FLIGHTS_WEB_WISHLIST_WIDGET_DESKTOP_SSR"] && isLoggedInAuthLow() ? (React.createElement(WishlistWidgetLoader, null)) : undefined))));
};
export default SearchInboundDesktop;
